import JSCookie from 'js-cookie';
import { ref, computed, onBeforeUnmount } from 'vue-demi';
import { useOneReachConfig } from '@/composables';

export type Theme = 'light' | 'dark';
const appTheme = ref('' as Theme);
const isDarkMode = computed(() => appTheme.value === 'dark');

/**
 * Track theme changes based on cookies.
 * Allow switching themes using "CMD + Shift + D" key.
 */
export const useAppThemeTracking = () => {
  const ORConfig = useOneReachConfig().getORConfig();
  const THEME_COOKIE_CHECK_INTERVAL = 1000;
  const THEME_DARK_CLASS = 'dark';
  const THEME_LIGHT_CLASS = 'light';
  const THEME_COOKIE = `${ORConfig.CLIENT}_${ORConfig.ENV}_dark_mode`;
  const timer = ref(0 as number);

  const getTheme = () => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const value = JSCookie.get(THEME_COOKIE) || (mq.matches ? '1' : '0');
    return value === '1' ? 'dark' : 'light';
  };

  const setTheme = (args: { theme: Theme; cookie?: boolean; }) => {
    const { theme, cookie } = args;

    switch (theme) {
      case 'dark':
        document.documentElement.classList.remove(THEME_LIGHT_CLASS);
        document.documentElement.classList.add(THEME_DARK_CLASS);
        appTheme.value = 'dark';
        break;
      case 'light':
      default:
        document.documentElement.classList.remove(THEME_DARK_CLASS);
        document.documentElement.classList.add(THEME_LIGHT_CLASS);
        appTheme.value = 'light';
        break;
    }

    if (cookie) {
      const value = theme === 'dark' ? '1' : '0';
      JSCookie.set(THEME_COOKIE, value, { domain: ORConfig.COOKIE_DOMAIN });
    }
  };

  const toggleThemeKeydownHandler = (e: KeyboardEvent) => {
    const SHORTCUT_MAIN_KEY = 'd';
    if (e.metaKey && e.shiftKey && e.key === SHORTCUT_MAIN_KEY) {
      e.preventDefault();
      const theme = getTheme();
      setTheme({ theme: theme === 'dark' ? 'light' : 'dark', cookie: true });
    }
  };

  const initializeThemeTracking = () => {
    setTheme({ theme: getTheme() });

    document.addEventListener('keydown', toggleThemeKeydownHandler);
    timer.value = window.setInterval(() => {
      setTheme({ theme: getTheme() });
    }, THEME_COOKIE_CHECK_INTERVAL);
  };

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', toggleThemeKeydownHandler);
    window.clearInterval(timer.value);
  });

  const toggleTheme = () => {
    const currentTheme = getTheme();
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setTheme({ theme: newTheme, cookie: true });
    return newTheme;
  };

  return {
    appTheme,
    isDarkMode,
    initializeThemeTracking,
    toggleTheme,
  };
};
